import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import DropZone from '../../components/DropZone';
import { FileListItem } from '../../components/FileListItem';
import {
  MODEL_EXTENSIONS,
  INITIALIZATION_EXTENSIONS,
  PARAMETER_EXTENSIONS,
  COMPRESSION_EXTENSIONS,
  ALL_INPUTFILE_EXTENSIONS,
} from '../../services/BatchFileValidator';

import './UploadDialog.scss';

export default () => {
  class UploadDialog extends React.Component {
    static propTypes = {
      onClose: PropTypes.func.isRequired,
      open: PropTypes.bool.isRequired,
      apiClient: PropTypes.func.isRequired,
      repositoryService: PropTypes.shape({
        getUploader: PropTypes.func.isRequired,
        fetchAllObjects: PropTypes.func.isRequired,
      }).isRequired,
      authService: PropTypes.shape({
        getUsername: PropTypes.func.isRequired,
      }).isRequired,
    };

    constructor(props) {
      super(props);

      const { repositoryService } = props;
      this.allObjects = [];
      this.uploader = repositoryService.getUploader();

      this.state = this.getInitialState();

      // Bindings
      this.handleUpload = this.handleUpload.bind(this);
      this.handleCancel = this.handleCancel.bind(this);
      this.handleAbort = this.handleAbort.bind(this);
      this.handleHide = this.handleHide.bind(this);
      this.handleContainerChange = this.handleContainerChange.bind(this);
      this.handleNodesChanged = this.handleNodesChanged.bind(this);
      this.handleFilesAdded = this.handleFilesAdded.bind(this);
      this.handleDeleteFile = this.handleDeleteFile.bind(this);

      const modelFileExtensions = MODEL_EXTENSIONS.concat(INITIALIZATION_EXTENSIONS, PARAMETER_EXTENSIONS);
      this.invalidExtensionMessage = `Invalid file extension(s).\nAccepted files are ${modelFileExtensions.concat(COMPRESSION_EXTENSIONS).join(', ')}`;
      this.inputFileExtensions = ALL_INPUTFILE_EXTENSIONS;
      this.fileSetValidator = {
        validateFile: (node, ctx) => {
          if (this.allObjects) {
            const container = ctx.container ? ctx.container : this.state.container;
            const sameObj = this.allObjects.find((obj) => {
              return (obj.container === container) && (obj.name === node.name);
            });
            if (sameObj) {
              return 'Object with same name and container already exists';
            }
          }
          return null;
        },
      };
      this.uploadSubscriber = {
        filesChanged: (nodes) => {
          this.setState({ nodes });
        },
        uploadStarted: () => {
          this.setState({ uploading: true });
        },
        uploadEnded: () => {
          this.setState({ uploading: false });
        },
        uploadFailed: () => {
          this.setState({ uploading: false });
        },
      };
    }

    getInitialState() {
      const { authService } = this.props;
      return {
        error: '',
        container: `${authService.getUsername()}_${moment().format('YYYYMMDD_HHmmss')}`,
        nodes: this.uploader.getFileNodes(),
        uploading: this.uploader.isUploading(),
      };
    }

    componentDidMount() {
      this.uploader.addValidator(this.fileSetValidator);
      this.uploader.subscribeUpload(this.uploadSubscriber);
      if (this.uploader.validateFiles()) {
        this.setState({ nodes: this.uploader.getFileNodes() });
      }
    }

    componentDidUpdate(prevProps) {
      if (this.props.open && !prevProps.open) {
        // Update the default values and validation errors each time the dialog is showing
        // Example for using this method for this use case can be found here:
        // https://reactjs.org/docs/react-component.html#componentdidupdate
        this.setState(this.getInitialState(), () => {
          this.loadObjects();
        });
      }
    }

    componentWillUnmount() {
      this.uploader.removeValidator(this.fileSetValidator);
      this.uploader.unsubscribeUpload(this.uploadSubscriber);
    }

    loadObjects() {
      const { repositoryService } = this.props;
      repositoryService.fetchAllObjects().then((objects) => {
        this.allObjects = objects;
        const { nodes } = this.state;
        if (this.uploader.validateFiles()) {
          // Forces the DropZone component to update
          this.setState({ nodes: nodes.slice() });
        }
      });
    }

    handleCancel() {
      this.uploader.removeFiles(this.uploader.getFileNodes()).catch(err => err).then(() => {
        this.setState(this.getInitialState(), () => {
          this.props.onClose(null);
        });
      });
    }

    handleUpload() {
      this.uploader.upload({ container: this.state.container })
      .then((successNodes) => {
        return this.uploader.removeFiles(successNodes).then(() => successNodes);
      })
      .then((successNodes) => {
        if (!this.uploader.getFileNodes().find(n => n.error)) {
          this.props.onClose(successNodes);
        }
      })
      .catch(() => {
        const successNodes = this.uploader.getFileNodes().filter(node => node.isDone());
        return this.uploader.removeFiles(successNodes);
      });
    }

    handleAbort() {
      this.uploader.abort();
    }

    handleHide() {
      this.props.onClose();
    }

    handleContainerChange(event) {
      const { nodes } = this.state;
      const container = event.target.value;
      this.setState({
        // Forces the DropZone component to update if node errors changed
        nodes: this.uploader.validateFiles({ container }) ? nodes.slice() : nodes,
        container,
      });
    }

    handleNodesChanged(nodes) {
      this.setState({ nodes });
    }

    handleFilesAdded(files) {
      this.uploader.addFiles(files);
    }

    handleDeleteFile(node) {
      this.uploader.removeFiles([node]);
    }

    render() {
      const { onClose, open, repositoryService } = this.props;
      const { container, nodes, uploading } = this.state;
      return (
        <Dialog
          disableBackdropClick
          disableEscapeKeyDown
          aria-labelledby="upload-dialog-title"
          className="grb-upload-dialog"
          onClose={onClose}
          open={open}
        >
          <DialogTitle id="upload-dialog-title">Upload objects</DialogTitle>
          <DialogContent>
            <form onSubmit={this.handleSubmit} className="grb-upload-dialog-body">
              <TextField
                label="Container"
                className="grb-uploadcontainer-textfield"
                value={container}
                fullWidth
                onChange={this.handleContainerChange}
              />
              <DropZone
                {...this.props}
                onFilesAdded={this.handleFilesAdded}
                validExtensions={this.inputFileExtensions}
                invalidExtensionMessage={this.invalidExtensionMessage}
              />
              <List>
                {nodes.map(node => (
                  <FileListItem
                    key={node.name}
                    node={node}
                    onDelete={this.handleDeleteFile}
                    uploader={this.uploader}
                    repositoryService={repositoryService}
                  />
                ))}
              </List>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={uploading ? this.handleAbort : this.handleCancel} color="primary">
              {uploading ? 'Abort' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!uploading && (nodes.length === 0 || !!nodes.find(node => node.validationError))}
              onClick={uploading ? this.handleHide : this.handleUpload}
            >
              {uploading ? 'Hide' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>
      );
    }
  }
  return UploadDialog;
};
